import { useEffect } from 'react';
import type { NetFeesReport, WithdrawalFee } from './withdrawalMode.utils';

export type UseNetFeesReportProp = {
  isNetWithdrawal: boolean;
  isFxRateReady: boolean;
  loading: boolean;
  hasFeesError: boolean;
  fees: WithdrawalFee[] | undefined;
  accountId: string | undefined;
  netCents: number;
  convertFeeFromBaseCurrency: (baseCurrencyCents: number) => number;
  onNetFeesChange: ((report: NetFeesReport | null) => void) | undefined;
};

export const useNetFeesReport = ({
  isNetWithdrawal,
  isFxRateReady,
  loading,
  hasFeesError,
  fees,
  accountId,
  netCents,
  convertFeeFromBaseCurrency,
  onNetFeesChange,
}: UseNetFeesReportProp) => {
  useEffect(() => {
    if (!isNetWithdrawal || !onNetFeesChange) return;

    // Fees computed on a fallback rate pick the wrong withholding tier, and the
    // gross derived from them would be submitted as if it were right.
    if (!isFxRateReady) {
      onNetFeesChange(null);
      return;
    }

    // A report left from an earlier query matches the same account and net amount,
    // so it would enable Continue before this query finishes, or after it fails.
    if (loading || hasFeesError || !fees) {
      onNetFeesChange(null);
      return;
    }

    onNetFeesChange({
      accountId,
      netCents,
      fees: fees.map((fee) => ({
        ...fee,
        amountCents: convertFeeFromBaseCurrency(fee.amountCents),
      })),
    });
  }, [
    fees,
    loading,
    hasFeesError,
    isNetWithdrawal,
    isFxRateReady,
    accountId,
    netCents,
    convertFeeFromBaseCurrency,
    onNetFeesChange,
  ]);
};
